import React, { HTMLAttributes, ReactElement, useState } from 'react';
import classNames from 'classnames';

import Svg from 'atomic/svg/svg';
import Dropdown from 'atomic/dropdown/dropdown';
import Summary from 'atomic/summary/summary';
import Button from 'atomic/button/button';
import Loading from 'atomic/loading/loading';
import { useFetch } from 'hooks/use-fetch';
import { RankingGeneralByTypeInterface, RankingGeneralInterface } from 'interfaces/ranking-general.interface';

import './main-ranking.scss';

const FADE_DURATION = 200; // ms

export interface MainRankingInterface {
    icon: string;
    model: 'company' | 'product';
    bestRatingTypeBase: string;
    bestRatingItems: RankingGeneralInterface[];
    mostRatedTypeBase: string;
    mostRatedItems: RankingGeneralInterface[];
}

enum ActiveTab {
    bestRating,
    mostRated
}

interface MainRankingProps extends MainRankingInterface, HTMLAttributes<HTMLElement> {
    limit?: number;
}

const MainRanking: React.FC<MainRankingProps> = (
    {
        icon,
        model,
        className,
        bestRatingTypeBase,
        bestRatingItems,
        mostRatedTypeBase,
        mostRatedItems,
        limit = 10,
        children,
        ...props
    }
): ReactElement => {
    const rankingClasses = classNames(className, 'main-ranking');

    const [activeTab, setActiveTab] = useState(ActiveTab.bestRating);
    const [isBestRatingVisible, setIsBestRatingVisible] = useState(true);
    const [isMostRatedVisible, setIsMostRatedVisible] = useState(false);

    const [bestRatingList, setBestRatingList] = useState(bestRatingItems);
    const [mostRatedList, setMostRatedList] = useState(mostRatedItems);

    const { get, loading } = useFetch(`/rankings/${model}`, { method: 'GET' });

    const handleClickTab = (tab: ActiveTab): void => {
        if (activeTab === ActiveTab.bestRating && tab === ActiveTab.bestRating
            || activeTab === ActiveTab.mostRated && tab === ActiveTab.mostRated) {
            return;
        }

        if (tab === ActiveTab.bestRating) {
            setIsMostRatedVisible(false);
            setTimeout(() => setActiveTab(ActiveTab.bestRating), FADE_DURATION);
            setTimeout(() => setIsBestRatingVisible(true), FADE_DURATION + 1);
        } else {
            setIsBestRatingVisible(false);
            setTimeout(() => setActiveTab(ActiveTab.mostRated), FADE_DURATION);
            setTimeout(() => setIsMostRatedVisible(true), FADE_DURATION + 1);
        }
    };

    const handleDropdownChange = (value: string | number): void => {
        get({ types: `${bestRatingTypeBase}${value},${mostRatedTypeBase}${value}` }).then((response: RankingGeneralByTypeInterface) => {
            setBestRatingList(response[`${bestRatingTypeBase}${value}`]);
            setMostRatedList(response[`${mostRatedTypeBase}${value}`]);
        });
    };

    const bestRatingElements = bestRatingList.slice(0, limit > 0 ? limit : 10).map(
        ({ placeNumber, name, url, imageUrl, reviewAverageRating, reviewTotalCount }: RankingGeneralInterface): ReactElement => (
            <Summary
                className="rankings__list-item"
                key={placeNumber}
                title={name}
                url={url}
                average={reviewAverageRating}
                count={reviewTotalCount}
                image={imageUrl}
            />
        )
    );

    const mostRatedElements = mostRatedList.slice(0, limit > 0 ? limit : 10).map(
        ({ placeNumber, name, url, imageUrl, reviewAverageRating, reviewTotalCount }: RankingGeneralInterface): ReactElement => (
            <Summary
                className="rankings__list-item"
                key={placeNumber}
                title={name}
                url={url}
                average={reviewAverageRating}
                count={reviewTotalCount}
                image={imageUrl}
            />
        )
    );

    return (
        <div className={rankingClasses} {...props}>
            {loading && <Loading />}
            <div className="main-ranking__top">
                <h2 className="main-ranking__title">
                    <Svg className="main-ranking__title-icon" path={icon} width={28}
                        height={28} fill="#003B6D"
                    />
                    <span className="main-ranking__title-text">
                        {children}
                    </span>
                </h2>
                <Dropdown
                    className="main-ranking__dropdown"
                    value="30day"
                    list={[
                        { value: '30day', title: 'Ostatni miesiąc' },
                        { value: '90day', title: 'Ostatnie 3 miesiące' },
                        { value: '1year', title: 'Ostatni rok' }
                    ]}
                    readOnly={true}
                    onDropdownChange={handleDropdownChange}
                />
            </div>
            <div className="main-ranking__filter">
                <Button
                    className={classNames('main-ranking__filter-best', { 'main-ranking__filter-best--active': activeTab === ActiveTab.bestRating })}
                    wrapper={true}
                    onClick={(): void => handleClickTab(ActiveTab.bestRating)}
                >
                    Najlepiej oceniane
                </Button>
                <Button
                    className={classNames('main-ranking__filter-frequently', { 'main-ranking__filter-frequently--active': activeTab === ActiveTab.mostRated })}
                    wrapper={true}
                    onClick={(): void => handleClickTab(ActiveTab.mostRated)}
                >
                    Najczęściej oceniane
                </Button>
            </div>
            <div
                className={classNames(
                        'main-ranking__results',
                        {
                            'main-ranking__results--active': activeTab === ActiveTab.bestRating,
                            'main-ranking__results--visible': isBestRatingVisible
                        }
                )}
            >
                {bestRatingElements}
            </div>
            <div
                className={classNames(
                        'main-ranking__results',
                        {
                            'main-ranking__results--active': activeTab === ActiveTab.mostRated,
                            'main-ranking__results--visible': isMostRatedVisible
                        }
                )}
            >
                {mostRatedElements}
            </div>
        </div>
    );
};

export default MainRanking;
